/**
 * Gemini Live session for the floating onboarding agent. Purpose-built (separate
 * from the meeting-simulation useGeminiLive) so the critical live-meeting path is
 * untouched. Differences: it exposes function-calling (navigate / highlight),
 * does NOT write to any meeting transcript, and reports a "speaking" signal for
 * the talking visualizer.
 *
 * Runs entirely browser-direct on the user's own Gemini key (ADR 0004), same as
 * Quiet ask and the simulation.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { effectiveGeminiKey, ensureGeminiKey } from "../../lib/geminiKey.ts";

// Gemini Live requires native-audio models for bidirectional WebSocket streaming.
const PRIMARY_MODEL =
  (import.meta.env.VITE_LIVE_MODEL as string | undefined) || "models/gemini-2.5-flash-native-audio-latest";
const LIVE_MODELS = [
  { ver: "v1beta", model: PRIMARY_MODEL },
  { ver: "v1beta", model: "models/gemini-2.5-flash-native-audio-preview-12-2025" },
  { ver: "v1alpha", model: "models/gemini-2.5-flash-native-audio-latest" },
];

// Tools the agent may call to guide the user through the UI.
const TOOLS = [
  {
    functionDeclarations: [
      {
        name: "readScreen",
        description:
          "Read what is ACTUALLY on the user's screen right now — the current screen, headings, the clickable buttons/links/tabs, and what the user is hovering. ALWAYS call this before giving directions, naming a button, or answering 'where/how do I…', so your guidance matches the real UI instead of guessing.",
        parameters: { type: "OBJECT", properties: {} },
      },
      {
        name: "navigate",
        description:
          "Take the user to a screen in the SellSynq app (dashboard, clients, training, simulations, tasks, discovery, marketplace, developers, settings) or switch in-meeting tabs (infographic, diagram, decisiontree, whatsnew, usecase, transcript, captions). Use when guiding them somewhere.",
        parameters: {
          type: "OBJECT",
          properties: {
            to: {
              type: "STRING",
              description:
                "Screen or tab key: dashboard, clients, training, simulations, tasks, discovery, marketplace, developers, settings, infographic, diagram, decisiontree, whatsnew, usecase, transcript, or captions.",
            },
          },
          required: ["to"],
        },
      },
      {
        name: "switchMeetingTab",
        description:
          "Switch between tabs in the live meeting right panel: 'infographic' (visual summary), 'diagram' (cloud architecture), 'decisiontree' (logic/decision flow), 'whatsnew' (latest SellSynq updates), 'usecase' (customer stories/examples), 'transcript' (spoken conversation), or 'captions' (live translated subtitles). Call this whenever the user asks to see/open any of these tabs during a meeting.",
        parameters: {
          type: "OBJECT",
          properties: {
            tab: {
              type: "STRING",
              description:
                "Tab name: infographic, diagram, decisiontree, whatsnew, usecase, transcript, or captions.",
            },
          },
          required: ["tab"],
        },
      },
      {
        name: "highlight",
        description:
          "Visually point at an on-screen button or link so the user can find it. Pass the visible label text.",
        parameters: {
          type: "OBJECT",
          properties: {
            text: {
              type: "STRING",
              description: "The visible label of the button/link to highlight, e.g. 'Quick meeting'.",
            },
          },
          required: ["text"],
        },
      },
      {
        name: "showWhatsNew",
        description:
          "Open Sally's 'What's new' popup so the user can read the latest release notes. Call this when the user wants to see/hear what's new.",
        parameters: { type: "OBJECT", properties: {} },
      },
    ],
  },
];

export interface UseOnboardingLiveOptions {
  systemInstruction: string;
  /** Runs a tool call and returns a short status string fed back to the model. */
  onToolCall: (name: string, args: Record<string, unknown>) => string;
}

export interface OnboardingLiveHandle {
  connect: () => void;
  disconnect: () => void;
  sendAudio: (pcm: Int16Array) => void;
  /** Push current on-screen context to the model without triggering a response. */
  sendContext: (text: string) => void;
  /** Send a user turn that triggers a spoken response (e.g. the opening greeting). */
  sendUserText: (text: string) => void;
  connected: boolean;
  connecting: boolean;
  speaking: boolean;
  error: string | null;
}

function int16ToBase64(pcm: Int16Array): string {
  const bytes = new Uint8Array(pcm.buffer, pcm.byteOffset, pcm.byteLength);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}

function base64ToFloat32(b64: string): Float32Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const int16 = new Int16Array(bytes.buffer);
  const float32 = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i++) float32[i] = int16[i]! / 32768;
  return float32;
}

export function useOnboardingLive(opts: UseOnboardingLiveOptions): OnboardingLiveHandle {
  const { systemInstruction, onToolCall } = opts;
  const wsRef = useRef<WebSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const nextPlayTimeRef = useRef(0);
  const activeSourcesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const speakingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const systemRef = useRef(systemInstruction);
  const toolCallRef = useRef(onToolCall);
  const connectingRef = useRef(false);
  const isSetupCompleteRef = useRef(false);
  const reconnectTriesRef = useRef(0);

  useEffect(() => {
    systemRef.current = systemInstruction;
  }, [systemInstruction]);
  useEffect(() => {
    toolCallRef.current = onToolCall;
  }, [onToolCall]);

  const ensureAudioCtx = useCallback(() => {
    if (!audioCtxRef.current) audioCtxRef.current = new AudioContext({ sampleRate: 24000 });
    if (audioCtxRef.current.state === "suspended") void audioCtxRef.current.resume();
    return audioCtxRef.current;
  }, []);

  const markSpeaking = useCallback(() => {
    setSpeaking(true);
    if (speakingTimerRef.current) clearTimeout(speakingTimerRef.current);
    speakingTimerRef.current = setTimeout(() => setSpeaking(false), 350);
  }, []);

  const playAudioChunk = useCallback(
    (b64: string) => {
      const ctx = ensureAudioCtx();
      const samples = base64ToFloat32(b64);
      if (samples.length === 0) return;
      const buf = ctx.createBuffer(1, samples.length, 24000);
      buf.copyToChannel(samples, 0);
      const source = ctx.createBufferSource();
      source.buffer = buf;
      source.connect(ctx.destination);
      const startAt = Math.max(ctx.currentTime, nextPlayTimeRef.current);
      source.start(startAt);
      nextPlayTimeRef.current = startAt + buf.duration;
      activeSourcesRef.current.add(source);
      source.onended = () => activeSourcesRef.current.delete(source);
      markSpeaking();
    },
    [ensureAudioCtx, markSpeaking],
  );

  const stopPlayback = useCallback(() => {
    for (const source of activeSourcesRef.current) {
      try {
        source.onended = null;
        source.stop();
      } catch {
        /* already stopped */
      }
    }
    activeSourcesRef.current.clear();
    nextPlayTimeRef.current = 0;
    setSpeaking(false);
  }, []);

  const sendToolResponses = useCallback(
    (calls: Array<{ id?: string; name?: string; args?: Record<string, unknown> }>) => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      const functionResponses = calls.map((c) => {
        let result = "ok";
        try {
          result = toolCallRef.current(c.name ?? "", c.args ?? {});
        } catch {
          result = "The action failed.";
        }
        return { id: c.id, name: c.name, response: { result } };
      });
      ws.send(JSON.stringify({ toolResponse: { functionResponses } }));
    },
    [],
  );

  const connectWithModel = useCallback(
    async (modelIdx: number) => {
      let key = effectiveGeminiKey();
      if (!key) {
        key = await ensureGeminiKey();
      }
      if (!key) {
        setError("Could not load Gemini API key. Please check connection.");
        setConnecting(false);
        connectingRef.current = false;
        return;
      }
      const entry = LIVE_MODELS[modelIdx];
      if (!entry) {
        connectingRef.current = false;
        setConnecting(false);
        setError("Couldn't reach Gemini Live. Check your API key.");
        return;
      }

      const url = `wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.${entry.ver}.GenerativeService.BidiGenerateContent?key=${encodeURIComponent(key)}`;
      const timeout = setTimeout(() => {
        try {
          ws.close();
        } catch {
          /* noop */
        }
        connectWithModel(modelIdx + 1);
      }, 8000);

      const ws = new WebSocket(url);
      ws.binaryType = "arraybuffer";
      wsRef.current = ws;
      setError(null);

      ws.onopen = () => {
        ws.send(
          JSON.stringify({
            setup: {
              model: entry.model,
              generationConfig: {
                responseModalities: ["AUDIO"],
                speechConfig: {
                  voiceConfig: { prebuiltVoiceConfig: { voiceName: "Aoede" } },
                },
              },
              systemInstruction: { parts: [{ text: systemRef.current }] },
              tools: TOOLS,
            },
          }),
        );
      };

      const decoder = new TextDecoder();
      ws.onmessage = (ev) => {
        try {
          const raw =
            typeof ev.data === "string" ? ev.data : decoder.decode(ev.data as ArrayBuffer);
          const msg = JSON.parse(raw);

          if (msg.setupComplete) {
            clearTimeout(timeout);
            connectingRef.current = false;
            isSetupCompleteRef.current = true;
            reconnectTriesRef.current = 0;
            setConnecting(false);
            setConnected(true);
            setError(null);
            return;
          }

          if (msg.toolCall?.functionCalls) {
            sendToolResponses(msg.toolCall.functionCalls);
            return;
          }

          if (msg.serverContent?.interrupted) {
            stopPlayback();
            return;
          }

          const parts = msg.serverContent?.modelTurn?.parts;
          if (parts) {
            for (const part of parts) {
              if (part.inlineData?.data) playAudioChunk(part.inlineData.data);
            }
          }
        } catch {
          /* ignore malformed frame */
        }
      };

      ws.onclose = (ev) => {
        clearTimeout(timeout);
        setConnected(false);
        isSetupCompleteRef.current = false;
        if (ev.code !== 1000 && connectingRef.current) {
          connectWithModel(modelIdx + 1);
          return;
        }
        if (ev.code !== 1000) {
          // If the socket closed unexpectedly while the panel is still active,
          // try auto-reconnecting twice before surfacing an error to the user.
          if (reconnectTriesRef.current < 2) {
            reconnectTriesRef.current++;
            console.warn(
              `[onboarding-live] Unexpected close (code ${ev.code}, reason "${ev.reason}"). Auto-reconnecting (#${reconnectTriesRef.current})…`,
            );
            setTimeout(() => {
              connectWithModel(0);
            }, 1200);
            return;
          }
          setConnecting(false);
          setError(`Onboarding agent disconnected (${ev.reason || `code ${ev.code}`}).`);
        }
      };
    },
    [playAudioChunk, stopPlayback, sendToolResponses],
  );

  const connect = useCallback(() => {
    if (connectingRef.current || wsRef.current) return;
    ensureAudioCtx();
    reconnectTriesRef.current = 0;
    isSetupCompleteRef.current = false;
    connectingRef.current = true;
    setConnecting(true);
    connectWithModel(0);
  }, [connectWithModel, ensureAudioCtx]);

  const disconnect = useCallback(() => {
    connectingRef.current = false;
    reconnectTriesRef.current = 0;
    isSetupCompleteRef.current = false;
    setConnecting(false);
    if (wsRef.current) {
      try {
        wsRef.current.close(1000);
      } catch {
        /* noop */
      }
      wsRef.current = null;
    }
    setConnected(false);
    stopPlayback();
    if (audioCtxRef.current) {
      void audioCtxRef.current.close();
      audioCtxRef.current = null;
    }
  }, [stopPlayback]);

  const sendAudio = useCallback((pcm: Int16Array) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !isSetupCompleteRef.current) return;
    try {
      ws.send(
        JSON.stringify({
          realtimeInput: {
            audio: { mimeType: "audio/pcm;rate=16000", data: int16ToBase64(pcm) },
          },
        }),
      );
    } catch {
      /* ignore send failure */
    }
  }, []);

  // Push a fresh snapshot of what's on screen (including popups) WITHOUT asking
  // the model to respond (turnComplete:false). Lets the agent "see" the screen
  // change in real time — e.g. a dialog opening — not only when it calls readScreen.
  const sendContext = useCallback((text: string) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !isSetupCompleteRef.current || !text.trim()) return;
    try {
      ws.send(
        JSON.stringify({
          clientContent: {
            turns: [{ role: "user", parts: [{ text: `[SCREEN UPDATE — current view]\n${text}` }] }],
            turnComplete: false,
          },
        }),
      );
    } catch {
      /* ignore send failure */
    }
  }, []);

  // Send a user turn that DOES ask the model to respond (turnComplete:true) —
  // used to kick off the spoken greeting/menu as soon as the panel opens.
  const sendUserText = useCallback((text: string) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !isSetupCompleteRef.current || !text.trim()) return;
    try {
      ws.send(
        JSON.stringify({
          clientContent: {
            turns: [{ role: "user", parts: [{ text }] }],
            turnComplete: true,
          },
        }),
      );
    } catch {
      /* ignore send failure */
    }
  }, []);

  useEffect(() => {
    return () => {
      if (speakingTimerRef.current) clearTimeout(speakingTimerRef.current);
      if (wsRef.current) {
        try {
          wsRef.current.close(1000);
        } catch {
          /* noop */
        }
        wsRef.current = null;
      }
      if (audioCtxRef.current) {
        void audioCtxRef.current.close();
        audioCtxRef.current = null;
      }
    };
  }, []);

  return {
    connect,
    disconnect,
    sendAudio,
    sendContext,
    sendUserText,
    connected,
    connecting,
    speaking,
    error,
  };
}
